import { fetchWithAuth, getAccessToken } from '../AuthService';
import { AI_SERVICE_BASE_URL, AI_STREAMING_SERVICE_BASE_URL } from '../AiApiService';

/**
 * AI Audio Generation API service
 * 
 * Provides access to AI-powered audio content generation (podcasts and audiobooks).
 */
export const AiAudioGenerationApi = {
  /**
   * Generate audio from documents synchronously (podcast or audiobook)
   * @param {Object} data - Audio generation parameters
   * @param {number[]} data.document_ids - Array of document IDs to generate audio from
   * @param {string} data.format - Audio format: "podcast" or "audiobook"
   * @param {number} [data.endeavor_id] - Endeavor ID (optional)
   * @param {string} [data.title] - Audio title (optional)
   * @param {string} [data.description] - Audio description (optional)
   * @param {Object} [data.voice_config] - Voice configuration (optional)
   * @param {string} [data.custom_search_prompt] - Custom prompt for RAG vector search (optional)
   * @param {string} [data.depth_level="moderate"] - Podcast depth: "brief", "moderate", "comprehensive", or "exhaustive"
   * @param {string} [data.length_level="full"] - Audiobook length: "brief", "moderate", "comprehensive", or "full"
   * @returns {Promise<Object>} Generated audio with audio_content_id, stream_url, duration_seconds, cost_usd, etc.
   */
  generateAudio: async (data) => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/audio/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Generate audio with Server-Sent Events for real-time progress stages.
   * Uses raw fetch (not fetchWithAuth) because we need to stream the response body.
   *
   * @param {Object} data - Same payload as generateAudio
   * @param {function(string): void} onStage - Called with each stage name as it arrives
   * @returns {Promise<Object>} Final result object (same shape as generateAudio response)
   */
  generateAudioStream: async (data, onStage) => {
    const token = getAccessToken();
    const url = `${AI_STREAMING_SERVICE_BASE_URL}/api/audio/generate-stream`;
    console.log('[AudioStream] POST', url);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    const contentType = response.headers.get('content-type') || '';
    console.log('[AudioStream] status=%d content-type=%s', response.status, contentType);

    if (!response.ok) {
      // Try to read error body for better diagnostics
      let errBody = '';
      try { errBody = await response.text(); } catch (_) {}
      console.error('[AudioStream] Request failed:', response.status, errBody);
      throw new Error(`Audio stream request failed: ${response.status} – ${errBody || response.statusText}`);
    }

    // If the proxy returned JSON instead of SSE, parse it directly
    if (contentType.includes('application/json')) {
      console.log('[AudioStream] Response is JSON (not SSE), parsing directly');
      const json = await response.json();
      if (json.error) throw new Error(json.error);
      return json;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;
    let chunkCount = 0;

    const parseSSEMessages = (raw) => {
      const messages = raw.split('\n\n');
      const remaining = messages.pop(); // keep incomplete trailing chunk

      for (const msg of messages) {
        if (!msg.trim()) continue;

        let eventType = 'message';
        let eventData = '';

        for (const line of msg.split('\n')) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }

        if (!eventData) continue;

        try {
          const parsed = JSON.parse(eventData);

          if (eventType === 'stage') {
            console.log('[AudioStream] stage:', parsed.stage);
            if (onStage) onStage(parsed.stage);
          } else if (eventType === 'result') {
            console.log('[AudioStream] result received, keys:', Object.keys(parsed));
            finalResult = parsed;
          } else if (eventType === 'error') {
            console.error('[AudioStream] server error event:', parsed);
            throw new Error(parsed.error || 'Audio generation failed');
          } else {
            console.log('[AudioStream] unknown event type:', eventType);
          }
        } catch (parseErr) {
          if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
          console.error('[AudioStream] JSON parse error for event:', eventType, eventData.substring(0, 200));
        }
      }

      return remaining;
    };

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      chunkCount++;
      const chunk = decoder.decode(value, { stream: true });
      if (chunkCount <= 3 || chunk.includes('"result"') || chunk.includes('"error"')) {
        console.log('[AudioStream] chunk #%d (%d bytes): %s', chunkCount, chunk.length, chunk.substring(0, 300));
      }
      buffer += chunk;
      buffer = parseSSEMessages(buffer);
    }

    // Flush any remaining data in the buffer (e.g. proxy stripped trailing \n\n)
    if (buffer.trim()) {
      console.log('[AudioStream] Flushing leftover buffer (%d bytes): %s', buffer.length, buffer.substring(0, 300));
      parseSSEMessages(buffer + '\n\n');
    }

    console.log('[AudioStream] Stream closed after %d chunks, result=%s', chunkCount, !!finalResult);

    if (!finalResult) {
      throw new Error('Audio stream ended without a result (received ' + chunkCount + ' chunks)');
    }

    return finalResult;
  },

  /**
   * Get status of an audio generation job
   * @param {string} jobId - Audio generation job ID
   * @returns {Promise<Object>} Job status with progress, result, or error
   */
  getAudioStatus: async (jobId) => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/audio/status/${jobId}`, {
      method: 'GET',
    });
  },

  /**
   * List all audio documents for the authenticated user
   * @returns {Promise<Object>} List of audio documents with metadata
   */
  listAudioDocuments: async () => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/audio/documents`, {
      method: 'GET',
    });
  },

  /**
   * Get a specific audio document with streaming URL
   * @param {number} documentId - Audio document ID
   * @returns {Promise<Object>} Audio document details with streaming URL
   */
  getAudioDocument: async (documentId) => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/audio/documents/${documentId}`, {
      method: 'GET',
    });
  },

  /**
   * Delete an audio document
   * @param {number} documentId - Audio document ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteAudioDocument: async (documentId) => {
    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/audio/documents/${documentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Direct text-to-speech synthesis.
   *
   * Sends raw text + voice gender to the AI-Service and waits for the audio
   * to be returned in the same response. Nothing is persisted server-side.
   *
   * @param {Object} params
   * @param {string} params.text - Text to read aloud.
   * @param {"male"|"female"} params.voice - Voice gender.
   * @param {AbortSignal} [params.signal] - Optional abort signal.
   * @returns {Promise<Blob>} MP3 audio blob.
   */
  textToSpeech: async ({ text, voice, signal } = {}) => {
    const token = getAccessToken();
    const response = await fetch(`${AI_SERVICE_BASE_URL}/api/audio/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text, voice }),
      signal,
    });

    if (!response.ok) {
      let message = `TTS request failed (${response.status})`;
      try {
        const errBody = await response.json();
        if (errBody && errBody.error) message = errBody.error;
      } catch (_) {
        // Non-JSON error body; keep default message.
      }
      const err = new Error(message);
      err.status = response.status;
      throw err;
    }

    // Force the blob type to audio/mpeg in case the server's Content-Type
    // header was stripped by a proxy. Without an explicit type, some browsers
    // fail to play the resulting object URL.
    const rawBlob = await response.blob();
    return new Blob([rawBlob], { type: 'audio/mpeg' });
  },
};
